/* eslint-disable */
// @ts-nocheck

import { ChevronDown, Search } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useDebounce } from '@/hooks/useDebounce'

export interface AsyncSelectOption {
  id: string | number
  label: string
  subtitle?: string
}

interface AsyncSelectFormProps extends UseControllerProps {
  label?: string
  fetcher: (search: string) => Promise<AsyncSelectOption[]>
  placeholder?: string
  multiple?: boolean
  notFoundMessage?: string
  disabled?: boolean
  defaultValue?: any
}

const AsyncSelectForm = React.forwardRef<
  HTMLButtonElement,
  AsyncSelectFormProps
>(
  (
    {
      name,
      control,
      label,
      fetcher,
      placeholder = 'Buscar...',
      multiple = false,
      notFoundMessage,
      disabled = false,
      defaultValue,
      ...rest
    },
    ref
  ) => {
    const { field, fieldState } = useController({ name, control, ...rest })
    const [isPopoverOpen, setIsPopoverOpen] = useState(false)
    const [searchQuery, setSearchQuery] = useState('')
    const debouncedSearch = useDebounce(searchQuery, 400)
    const queryClient = useQueryClient()

    // --- CACHE LOCAL DE BÚSQUEDAS ---
    // Clave: `${name}:${debouncedSearch}`
    const staticCache = (AsyncSelectForm as any)._searchCache as
      | Map<string, AsyncSelectOption[]>
      | undefined
    if (!staticCache) {
      ;(AsyncSelectForm as any)._searchCache = new Map<
        string,
        AsyncSelectOption[]
      >()
    }
    const searchCache: Map<string, AsyncSelectOption[]> = (
      AsyncSelectForm as any
    )._searchCache

    // ---
    const fetchWithCache = async (
      search: string
    ): Promise<AsyncSelectOption[]> => {
      const cacheKey = `${name}:${search}`
      if (searchCache.has(cacheKey)) {
        return searchCache.get(cacheKey)!
      }
      const result = await fetcher(search)
      searchCache.set(cacheKey, result)
      return result
    }

    const { data: options = [], isLoading } = useQuery<AsyncSelectOption[]>({
      queryKey: [name, debouncedSearch],
      queryFn: () => fetchWithCache(debouncedSearch),
      staleTime: 30 * 60 * 1000, // 30 minutos
    })

    // --- Manejar defaultValue inicial ---
    useEffect(() => {
      if (defaultValue) {
        // Para múltiple (categorías)
        if (multiple && Array.isArray(defaultValue)) {
          const defaultOptions: AsyncSelectOption[] = defaultValue.map(
            (item) => ({
              id: item.id,
              label: item.name,
              subtitle: item.description || item.observations,
            })
          )

          // Agregar al cache para búsqueda vacía
          const cacheKey = `${name}:`
          const currentOptions = searchCache.get(cacheKey) || []
          const newOptions = [...currentOptions]

          defaultOptions.forEach((defaultOption) => {
            if (!newOptions.some((opt) => opt.id === defaultOption.id)) {
              newOptions.unshift(defaultOption)
            }
          })

          searchCache.set(cacheKey, newOptions)

          // Invalidar query para mostrar las opciones inmediatamente
          queryClient.invalidateQueries({ queryKey: [name, debouncedSearch] })
        }
        // Para único (país)
        else if (
          !multiple &&
          typeof defaultValue === 'object' &&
          defaultValue.id &&
          defaultValue.name
        ) {
          const defaultOption: AsyncSelectOption = {
            id: defaultValue.id,
            label: defaultValue.name,
            subtitle:
              defaultValue.description ||
              defaultValue.observations ||
              defaultValue.code,
          }

          // Agregar al cache para búsqueda vacía
          const cacheKey = `${name}:`
          const currentOptions = searchCache.get(cacheKey) || []
          if (!currentOptions.some((opt) => opt.id === defaultOption.id)) {
            searchCache.set(cacheKey, [defaultOption, ...currentOptions])
            // Invalidar query para mostrar la opción inmediatamente
            queryClient.invalidateQueries({ queryKey: [name, debouncedSearch] })
          }
        }
      }
    }, [defaultValue, name, debouncedSearch, queryClient, multiple])

    // Para selección simple
    const selected = !multiple
      ? (() => {
          if (field.value === null || field.value === undefined) {
            return undefined
          }
          if (
            typeof field.value === 'object' &&
            field.value.id &&
            field.value.label
          ) {
            return field.value as AsyncSelectOption
          }
          const valueToCompare =
            typeof field.value === 'object' && field.value !== null
              ? field.value.id
              : field.value
          return (options as AsyncSelectOption[])
            .filter((opt) => opt !== null && opt !== undefined)
            .find((opt) => opt.id === valueToCompare)
        })()
      : Array.isArray(field.value)
        ? field.value.every(
            (item: any) =>
              typeof item === 'object' && item !== null && item.id && item.label
          )
          ? (field.value as AsyncSelectOption[])
          : (options as AsyncSelectOption[]).filter((opt) => {
              const currentFieldValues = Array.isArray(field.value)
                ? field.value
                : []
              const fieldIds = currentFieldValues
                .map((item: any) => {
                  if (item === null || item === undefined) {
                    return null
                  }
                  return typeof item === 'object' ? item.id : item
                })
                .filter((id) => id !== null && id !== undefined)
              return fieldIds.includes(opt.id)
            })
        : []

    const handleSelect = (option: AsyncSelectOption) => {
      if (multiple) {
        let updatedIds = Array.isArray(field.value)
          ? field.value
              .map((item: any) => {
                if (item === null || item === undefined) {
                  return null
                }
                return typeof item === 'object' ? item.id : item
              })
              .filter((id) => id !== null && id !== undefined)
          : []

        if (updatedIds.includes(option.id)) {
          updatedIds = updatedIds.filter((id) => id !== option.id)
        } else {
          updatedIds.push(option.id)
        }
        field.onChange(updatedIds)
      } else {
        field.onChange(option.id)
        setIsPopoverOpen(false)
      }
    }

    const isSelected = (option: AsyncSelectOption) => {
      if (multiple) {
        return (
          Array.isArray(field.value) &&
          field.value.some(
            (item: any) =>
              (typeof item === 'object' && item !== null ? item.id : item) ===
              option.id
          )
        )
      }
      return field.value === option.id
    }

    return (
      <div className={'w-full'}>
        <FormField
          control={control}
          name={name}
          render={() => (
            <FormItem>
              {label && <FormLabel>{label}</FormLabel>}
              <FormControl>
                <Popover
                  modal={false}
                  onOpenChange={setIsPopoverOpen}
                  open={isPopoverOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      aria-expanded={isPopoverOpen}
                      className="h-fit w-full justify-between font-normal"
                      disabled={disabled}
                      ref={ref}
                      role="combobox"
                      variant="outline"
                    >
                      {multiple ? (
                        <div className="flex flex-wrap gap-1">
                          {Array.isArray(selected) && selected.length > 0
                            ? selected.map((opt: AsyncSelectOption) => (
                                <span
                                  className="rounded bg-muted px-2 py-0.5 text-xs"
                                  key={opt.id}
                                >
                                  {opt.label}
                                </span>
                              ))
                            : 'Seleccionar...'}
                        </div>
                      ) : selected ? (
                        <span>{(selected as AsyncSelectOption).label}</span>
                      ) : (
                        'Seleccionar...'
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    avoidCollisions={true}
                    className="w-[--radix-popover-trigger-width] p-0"
                    collisionPadding={8}
                    side="bottom"
                    sideOffset={4}
                    style={{ zIndex: 9999 }}
                  >
                    <div className="px-3 pt-3">
                      <div className="relative mb-2">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          autoFocus
                          className="pl-9"
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder={placeholder}
                          value={searchQuery}
                        />
                      </div>
                    </div>
                    <div
                      className="max-h-[200px] overflow-y-auto"
                      onWheel={(e) => {
                        const target = e.currentTarget
                        target.scrollTop += e.deltaY
                      }}
                    >
                      {isLoading ? (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          Cargando...
                        </div>
                      ) : (options as AsyncSelectOption[]).length > 0 ? (
                        (options as AsyncSelectOption[]).map((option) => (
                          <div
                            className={`flex cursor-pointer items-center gap-2 p-2 hover:bg-muted ${isSelected(option) ? 'bg-accent' : ''}`}
                            key={option.id}
                            onClick={() => handleSelect(option)}
                          >
                            <span className="font-medium">{option.label}</span>
                            {isSelected(option) && (
                              <span className="ml-auto text-xs">✓</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-muted-foreground">
                          {notFoundMessage ?? 'No se encontraron resultados.'}
                        </div>
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </FormControl>
              <FormMessage>{fieldState.error?.message}</FormMessage>
            </FormItem>
          )}
        />
      </div>
    )
  }
)
export { AsyncSelectForm }
